import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ArrowLeft } from 'lucide-react';

const AboutUs = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800">A1C Survival Plan</span>
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-slate-600 hover:text-teal-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-slate prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-slate-800 mb-8">About Us</h1>

          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-3xl p-8 mb-8">
            <p className="text-xl text-slate-700 leading-relaxed mb-0">
              <span className="font-semibold text-teal-700">A1C Survival Plan</span> was created around a simple belief:
              <br />
              <span className="text-2xl font-medium text-slate-800 mt-2 block">
                living with diabetes isn't just about numbers — it's about daily life.
              </span>
            </p>
          </div>

          <hr className="my-10 border-slate-200" />

          <div className="space-y-4 text-slate-600 leading-relaxed">
            <p>
              It's about the meals you plan, the ones you rush through, and the ones you forget to log.
            </p>
            <p>
              It's about fitness schedules and calendars that look different every week.
            </p>
            <p>
              About medications taken on time, almost on time, or late because life got in the way.
            </p>
            <p>
              About sleep, stress, water, energy, and the quiet choices that add up over time.
            </p>
            <p className="font-medium text-slate-700">
              Most of it happens without ceremony — just day after day, doing your best.
            </p>
          </div>

          <hr className="my-10 border-slate-200" />

          <div className="space-y-4 text-slate-600 leading-relaxed">
            <p>
              For many people managing diabetes, doctor's visits are where it all comes together.
            </p>
            <p>
              You sit down, and the questions begin — not out of judgment, just routine.
            </p>
            <div className="bg-slate-50 rounded-2xl p-6 my-6 space-y-3">
              <p className="text-slate-700 italic">"What medications are you taking?"</p>
              <p className="text-slate-700 italic">"What are you eating?"</p>
              <p className="text-slate-700 italic">"How often do you work out?"</p>
              <p className="text-slate-700 italic">"Are you drinking enough water?"</p>
            </div>
            <p>
              And too often, the answers feel scattered — hard to recall, harder to explain, and easy to question.
            </p>
          </div>

          <hr className="my-10 border-slate-200" />

          <div className="space-y-4 text-slate-600 leading-relaxed">
            <p className="font-medium text-slate-800 text-lg">
              We built A1C Survival Plan because we couldn't find one place that brought everything together.
            </p>
            <p>Not just glucose.</p>
            <p>Not just food.</p>
            <p>Not just workouts or notes scribbled in different apps.</p>
            <p className="font-medium text-slate-700">
              But one clear picture — built from daily journaling, reflection, and simple tracking — that tells your real story over time.
            </p>
          </div>

          <hr className="my-10 border-slate-200" />

          <div className="bg-gradient-to-br from-slate-50 to-teal-50/30 rounded-3xl p-8 my-8">
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              <span className="font-semibold text-teal-700">At the center of the app is the journal.</span>
            </p>
            <p className="text-slate-600 leading-relaxed">
              Everything else connects back to it — nutrition, fitness, medications, hydration, mental health, and insights — so your daily life finally makes sense in context.
            </p>
          </div>

          <div className="space-y-2 text-slate-600 leading-relaxed">
            <p>No lectures.</p>
            <p>No judgment.</p>
            <p>No pretending diabetes fits neatly into a spreadsheet.</p>
          </div>

          <hr className="my-10 border-slate-200" />

          <div className="space-y-4 text-slate-600 leading-relaxed">
            <p className="font-medium text-slate-800 text-lg">
              A1C Survival Plan isn't about perfection.
            </p>
            <p>
              It's about awareness, consistency, and showing up informed — for yourself and for the people helping care for you.
            </p>
          </div>

          <div className="bg-gradient-to-br from-teal-500 to-cyan-500 rounded-3xl p-8 my-10 text-white">
            <p className="text-lg leading-relaxed mb-4">
              If you've ever wished for a better way to organize your story,
              <br />
              <span className="font-semibold">you're not alone.</span>
            </p>
            <p className="text-white/90 leading-relaxed mb-0">
              Our goal is simple: make it easier to understand your life with diabetes, one day at a time.
            </p>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-100 mt-16">
        <div className="max-w-4xl mx-auto px-6 py-8 text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} A1C Survival Plan. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AboutUs;
